import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Scale, Receipt } from "lucide-react";
import { money } from "@/lib/api";
import { Card } from "@/components/ui/card";

const GOLD = "hsl(45 85% 55%)";
const GREEN = "hsl(145 55% 45%)";
const PIE_COLORS = ["hsl(45 85% 55%)", "hsl(45 40% 40%)", "hsl(0 0% 70%)", "hsl(45 60% 70%)", "hsl(30 60% 50%)", "hsl(200 30% 55%)"];

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card data-testid={testid} className="border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">{label}</p>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <p className="mt-4 font-serif text-4xl" style={{ color: accent }}>
        {value}
      </p>
    </Card>
  );
}

const tooltipStyle = {
  background: "#0b0b0b",
  border: "1px solid hsl(45 10% 15%)",
  borderRadius: 8,
  color: "#eee",
  fontSize: 13,
};

export default function Overview({ summary }) {
  if (!summary) {
    return <div className="py-20 text-center text-muted-foreground" data-testid="overview-loading">Cargando resumen…</div>;
  }

  const eventData = summary.per_event.map((e) => ({
    name: e.name.length > 14 ? e.name.slice(0, 14) + "…" : e.name,
    Ingresos: e.income,
    Gastos: e.expense,
  }));

  return (
    <div className="space-y-8" data-testid="overview-panel">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testid="kpi-income" icon={TrendingUp} label="Ingresos totales" value={money(summary.total_income)} accent={GREEN} />
        <Kpi testid="kpi-expense" icon={TrendingDown} label="Gastos totales" value={money(summary.total_expense)} accent="hsl(0 72% 60%)" />
        <Kpi testid="kpi-balance" icon={Scale} label="Balance general" value={money(summary.balance)} accent={GOLD} />
        <Kpi testid="kpi-count" icon={Receipt} label="Transacciones" value={summary.transaction_count} accent="hsl(0 0% 85%)" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-6 font-serif text-2xl">Ingresos vs Gastos por Evento</h3>
          {eventData.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">Sin datos aún.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={eventData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(45 10% 15%)" vertical={false} />
                <XAxis dataKey="name" stroke="hsl(45 8% 58%)" fontSize={12} tickLine={false} />
                <YAxis stroke="hsl(45 8% 58%)" fontSize={12} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Bar dataKey="Ingresos" fill={GREEN} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Gastos" fill={GOLD} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="border-border bg-card p-6">
          <h3 className="mb-6 font-serif text-2xl">Gastos por Categoría</h3>
          {summary.expenses_by_category.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">Sin gastos aún.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={summary.expenses_by_category}
                  dataKey="amount"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  paddingAngle={3}
                >
                  {summary.expenses_by_category.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#0b0b0b" />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}
